/** Refund creation + Invoice generation для OrderShow (Phase C2 UX).
 *
 * Два независимых блока:
 *  - RefundsSection: список refund'ов заказа + кнопка «Создать возврат»
 *    (modal с line-items picker, amount, reason)
 *  - InvoicesSection: список invoice'ов + кнопка «Сгенерировать счёт»
 *    (POST /orders/{id}/invoices → file_url открывается в новой вкладке)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  FileBox,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/utils";

interface Refund {
  id: number;
  order_id: number;
  status: "pending" | "granted" | "sent" | "rejected";
  amount_tiyin: number;
  reason: string | null;
  line_items: Array<Record<string, unknown>>;
  granted_at: string | null;
  sent_at: string | null;
  provider_refund_id: string | null;
  created_at: string;
}

interface Invoice {
  id: number;
  order_id: number;
  number: string;
  file_url: string | null;
  created_at: string;
}

interface OrderItem {
  id: number;
  product_name: string;
  product_sku: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

const REFUND_STATUS_COLOR: Record<Refund["status"], string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  granted: "bg-blue-100 text-blue-800 border-blue-300",
  sent: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

// ─── Refunds ───────────────────────────────────────────────────────────

export function RefundsSection({
  orderId,
  orderItems,
}: {
  orderId: number;
  orderItems: OrderItem[];
}): JSX.Element {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);

  const { data: refunds, isLoading } = useQuery({
    queryKey: ["order-refunds", orderId],
    queryFn: async () => {
      const { data } = await api.get(`/refunds?order_id=${orderId}`);
      return data as Refund[];
    },
  });

  const grantMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/refunds/${id}/grant`);
    },
    onSuccess: () => {
      toast.success("Возврат одобрен");
      queryClient.invalidateQueries({ queryKey: ["order-refunds", orderId] });
    },
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileBox className="w-4 h-4" />
            Возвраты ({refunds?.length ?? 0})
          </CardTitle>
          <Button size="sm" onClick={() => setShowDialog(true)}>
            <Plus className="w-3 h-3 mr-1" />
            Создать возврат
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Загрузка…</p>
        ) : refunds?.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">
            Возвратов нет.
          </p>
        ) : (
          refunds?.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between gap-2 border rounded p-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className="font-mono text-xs">
                  #{r.id}
                </Badge>
                <Badge className={REFUND_STATUS_COLOR[r.status]}>
                  {r.status}
                </Badge>
                <span className="text-sm">{formatPrice(r.amount_tiyin)}</span>
                {r.reason && (
                  <span className="text-xs text-muted-foreground truncate">
                    {r.reason}
                  </span>
                )}
              </div>
              {r.status === "pending" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => grantMutation.mutate(r.id)}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Одобрить
                </Button>
              )}
            </div>
          ))
        )}
      </CardContent>

      <CreateRefundDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        orderId={orderId}
        orderItems={orderItems}
      />
    </Card>
  );
}

function CreateRefundDialog({
  open,
  onClose,
  orderId,
  orderItems,
}: {
  open: boolean;
  onClose: () => void;
  orderId: number;
  orderItems: OrderItem[];
}): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedQty, setSelectedQty] = useState<Record<number, number>>({});
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const computedAmount = Object.entries(selectedQty).reduce((sum, [itemId, qty]) => {
    if (!qty) return sum;
    const it = orderItems.find((x) => x.id === Number(itemId));
    if (!it) return sum;
    return sum + it.unit_price * qty;
  }, 0);

  const effectiveAmountTiyin = amount
    ? Math.round(Number(amount) * 100)
    : computedAmount;

  const createMutation = useMutation({
    mutationFn: async () => {
      const lineItems = Object.entries(selectedQty)
        .filter(([, q]) => q > 0)
        .map(([itemId, q]) => ({
          order_item_id: Number(itemId),
          quantity: q,
        }));
      await api.post("/refunds", {
        order_id: orderId,
        amount_tiyin: effectiveAmountTiyin,
        reason: reason.trim() || null,
        line_items: lineItems,
      });
    },
    onSuccess: () => {
      toast.success("Возврат создан");
      queryClient.invalidateQueries({ queryKey: ["order-refunds", orderId] });
      onClose();
      setSelectedQty({});
      setAmount("");
      setReason("");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const setQtyFor = (itemId: number, qty: number) => {
    setSelectedQty((prev) => ({ ...prev, [itemId]: qty }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Создать возврат</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">
              Позиции (выберите какие возвращаются)
            </Label>
            <div className="border rounded divide-y mt-1 max-h-64 overflow-auto">
              {orderItems.map((it) => {
                const qty = selectedQty[it.id] ?? 0;
                const checked = qty > 0;
                return (
                  <div
                    key={it.id}
                    className="flex items-center gap-2 p-2 text-sm"
                  >
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        const c = (e.target as HTMLInputElement).checked;
                        setQtyFor(it.id, c ? it.quantity : 0);
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">
                        {it.product_name}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        SKU: {it.product_sku} · {formatPrice(it.unit_price)}
                      </div>
                    </div>
                    {checked && (
                      <Input
                        type="number"
                        min={1}
                        max={it.quantity}
                        value={qty}
                        onChange={(e) =>
                          setQtyFor(
                            it.id,
                            Math.max(0, Math.min(it.quantity, Number(e.target.value))),
                          )
                        }
                        className="w-16 h-7 text-xs"
                      />
                    )}
                    <span className="text-xs text-muted-foreground w-20 text-right">
                      / {it.quantity}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">
                Сумма (сумы) — пусто = по позициям
              </Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`${Math.round(computedAmount / 100)}`}
              />
              <p className="text-[10px] text-muted-foreground">
                По позициям: {formatPrice(computedAmount)}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Будет создан возврат на</Label>
              <div className="border rounded px-3 py-2 text-sm font-semibold bg-muted/20">
                {formatPrice(effectiveAmountTiyin)}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Причина</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Например: «Брак при доставке»"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={
              effectiveAmountTiyin <= 0 || createMutation.isPending
            }
          >
            {createMutation.isPending && (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            )}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Invoices ──────────────────────────────────────────────────────────

export function InvoicesSection({ orderId }: { orderId: number }): JSX.Element {
  const queryClient = useQueryClient();

  const { data: invoices, isLoading } = useQuery({
    queryKey: ["order-invoices", orderId],
    queryFn: async () => {
      const { data } = await api.get(`/invoices?order_id=${orderId}`);
      return data as Invoice[];
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/orders/${orderId}/invoices`);
      return data as Invoice;
    },
    onSuccess: (data) => {
      toast.success(`Создан ${data.number}`);
      queryClient.invalidateQueries({ queryKey: ["order-invoices", orderId] });
      if (data.file_url) {
        window.open(data.file_url, "_blank", "noopener");
      }
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось"),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Счета ({invoices?.length ?? 0})
          </CardTitle>
          <Button
            size="sm"
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3 mr-1" />
            )}
            Сгенерировать
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Загрузка…</p>
        ) : invoices?.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">
            Счетов нет.
          </p>
        ) : (
          invoices?.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between gap-2 border rounded p-2 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className="font-mono text-xs">
                  {inv.number}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(inv.created_at).toLocaleString("ru-RU")}
                </span>
              </div>
              {inv.file_url ? (
                <a
                  href={inv.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  Скачать PDF →
                </a>
              ) : (
                <span className="text-xs text-muted-foreground">
                  в очереди
                </span>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
